package features.booking

import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.launch
import features.BaseViewModel
import kotlin.math.roundToInt

enum class BookingStep {
    BUSCAR_VIAJE,
    ELEGIR_ASIENTOS,
    LLENAR_MANIFIESTO,
    RESUMEN_COMPRA,
    VER_BOLETOS

}

/** Tramo del viaje al seleccionar asientos/pasajeros en una compra ida y vuelta. */
enum class Tramo { IDA, VUELTA }


class BookingViewModel(
    private val catalogRepository: CatalogRepository,
    private val repository: BookingRepository
) : BaseViewModel() {


    private val _currentStep = MutableStateFlow(BookingStep.BUSCAR_VIAJE)
    val currentStep: StateFlow<BookingStep> = _currentStep.asStateFlow()

    // FIX #6: MutableStateFlows ahora son privados con setters explícitos
    private val _origen = MutableStateFlow("")
    val origen: StateFlow<String> = _origen.asStateFlow()
    fun setOrigen(value: String) { _origen.value = value }

    private val _destino = MutableStateFlow("")
    val destino: StateFlow<String> = _destino.asStateFlow()
    fun setDestino(value: String) { _destino.value = value }

    private val _fecha = MutableStateFlow("")
    val fecha: StateFlow<String> = _fecha.asStateFlow()

    private val _horario = MutableStateFlow("")
    val horario: StateFlow<String> = _horario.asStateFlow()
    fun setHorario(value: String) { _horario.value = value }

    // ═══ Viaje de vuelta ═══
    private val _esIdaYVuelta = MutableStateFlow(false)
    val esIdaYVuelta: StateFlow<Boolean> = _esIdaYVuelta.asStateFlow()

    private val _fechaVuelta = MutableStateFlow("")
    val fechaVuelta: StateFlow<String> = _fechaVuelta.asStateFlow()

    private val _horarioVuelta = MutableStateFlow("")
    val horarioVuelta: StateFlow<String> = _horarioVuelta.asStateFlow()
    fun setHorarioVuelta(value: String) { _horarioVuelta.value = value }

    private val _horariosVueltaDisponibles = MutableStateFlow<List<String>>(emptyList())
    val horariosVueltaDisponibles: StateFlow<List<String>> = _horariosVueltaDisponibles.asStateFlow()

    // Tramo en curso durante la selección de asientos/pasajeros (ida primero, luego vuelta).
    private val _tramoActual = MutableStateFlow(Tramo.IDA)
    val tramoActual: StateFlow<Tramo> = _tramoActual.asStateFlow()

    // Datos guardados del tramo de IDA al pasar a la VUELTA (cada tramo tiene su propio layout/asientos/pasajeros).
    private var asientosIdaGuardados: List<Int> = emptyList()
    private var pasajerosIdaGuardados: List<PasajeroInfo> = emptyList()
    private var puntoSubidaIdaGuardado: String = ""
    // Datos del tramo de VUELTA (se llenan al confirmar el manifiesto de vuelta).
    private var asientosVueltaGuardados: List<Int> = emptyList()
    private var pasajerosVueltaGuardados: List<PasajeroInfo> = emptyList()
    private var puntoSubidaVueltaGuardado: String = ""

    // Ruta/fecha/horario del tramo que se está editando ahora mismo (ida o vuelta).
    private fun rutaTramo(): String = if (_tramoActual.value == Tramo.VUELTA)
        "${_destino.value} - ${_origen.value}" else "${_origen.value} - ${_destino.value}"
    private fun fechaTramo(): String = if (_tramoActual.value == Tramo.VUELTA) _fechaVuelta.value else _fecha.value
    private fun horarioTramo(): String = if (_tramoActual.value == Tramo.VUELTA) _horarioVuelta.value else _horario.value
    private val _horariosDisponibles = MutableStateFlow<List<String>>(emptyList())
    val horariosDisponibles: StateFlow<List<String>> = _horariosDisponibles.asStateFlow()

    private val _ciudades = MutableStateFlow<List<String>>(emptyList())
    val ciudades: StateFlow<List<String>> = _ciudades.asStateFlow()
    private val _vehiculos = MutableStateFlow<List<String>>(emptyList())
    val vehiculos: StateFlow<List<String>> = _vehiculos.asStateFlow()

    private val _paradas = MutableStateFlow<List<String>>(emptyList())
    val paradas: StateFlow<List<String>> = _paradas.asStateFlow()

    private val _paradasInfo = MutableStateFlow<List<ParadaInfo>>(emptyList())

    private val _whatsappUrl = MutableStateFlow<String?>(null)
    val whatsappUrl: StateFlow<String?> = _whatsappUrl.asStateFlow()

    private val _errorFormulario = MutableStateFlow<String?>(null)
    val errorFormulario: StateFlow<String?> = _errorFormulario.asStateFlow()

    private val _precios = MutableStateFlow<Map<String, Int>>(emptyMap())
    val precios: StateFlow<Map<String, Int>> = _precios.asStateFlow()

    private val _puntoSubida = MutableStateFlow("")
    val puntoSubida: StateFlow<String> = _puntoSubida.asStateFlow()

    private val _puntosSubidaViaje = MutableStateFlow<List<String>>(emptyList())
    val puntosSubidaViaje: StateFlow<List<String>> = _puntosSubidaViaje.asStateFlow()

    private val _puntosSubidaInfo = MutableStateFlow<List<ParadaInfo>>(emptyList())
    val puntosSubidaInfo: StateFlow<List<ParadaInfo>> = _puntosSubidaInfo.asStateFlow()

    private val _urlSalida = MutableStateFlow("")
    val urlSalida: StateFlow<String> = _urlSalida.asStateFlow()

    // ═══ Facturación ═══
    private val _requiereFactura = MutableStateFlow(false)
    val requiereFactura: StateFlow<Boolean> = _requiereFactura.asStateFlow()

    private val _rfcFactura = MutableStateFlow("")
    val rfcFactura: StateFlow<String> = _rfcFactura.asStateFlow()
    fun setRfcFactura(value: String) { _rfcFactura.value = value }

    private val _razonSocialFactura = MutableStateFlow("")
    val razonSocialFactura: StateFlow<String> = _razonSocialFactura.asStateFlow()
    fun setRazonSocialFactura(value: String) { _razonSocialFactura.value = value }

    private val _regimenFiscalFactura = MutableStateFlow("")
    val regimenFiscalFactura: StateFlow<String> = _regimenFiscalFactura.asStateFlow()
    fun setRegimenFiscalFactura(value: String) { _regimenFiscalFactura.value = value }

    private val _usoCfdiFactura = MutableStateFlow("")
    val usoCfdiFactura: StateFlow<String> = _usoCfdiFactura.asStateFlow()
    fun setUsoCfdiFactura(value: String) { _usoCfdiFactura.value = value }

    private val _cpFiscalFactura = MutableStateFlow("")
    val cpFiscalFactura: StateFlow<String> = _cpFiscalFactura.asStateFlow()
    fun setCpFiscalFactura(value: String) { _cpFiscalFactura.value = value }

    fun toggleRequiereFactura(requiere: Boolean) {
        _requiereFactura.value = requiere
        if (!requiere) {
            _rfcFactura.value = ""
            _razonSocialFactura.value = ""
            _regimenFiscalFactura.value = ""
            _usoCfdiFactura.value = ""
            _cpFiscalFactura.value = ""
        }
    }

    private val _whatsappSoporte = MutableStateFlow("")
    val whatsappSoporte: StateFlow<String> = _whatsappSoporte.asStateFlow()


    init {
        cargarCiudades()
        cargarParadas()
        cargarPrecios()
        cargarWhatsapp()
        instanciaActiva = this
    }

    private fun cargarWhatsapp() {
        viewModelScope.launch {
            try {
                val snap = network.FirebaseClient.database.reference("catalogos/configuracion/whatsapp").valueEvents.first()
                if (snap.exists) _whatsappSoporte.value = snap.value<String>()
            } catch (e: Exception) { /* usa fallback */ }
        }
    }

    private fun cargarCiudades() {
        viewModelScope.launch {
            try {
                val listaCiudades = catalogRepository.getCatalog("ciudades")
                _ciudades.value = listaCiudades
            } catch (e: Exception) {
                _ciudades.value = emptyList()
            }
        }
    }

    private fun cargarParadas() {
        viewModelScope.launch {
            try {
                val lista = catalogRepository.getCatalog("paradas")
                _paradas.value = lista
                // Cargar info enriquecida de paradas
                try {
                    val db = network.FirebaseClient.database
                    val snap = db.reference("catalogos/paradasInfo").valueEvents.first()
                    if (snap.exists) {
                        val infoList = mutableListOf<ParadaInfo>()
                        snap.children.forEach { child ->
                            val nombre = child.key ?: return@forEach
                            val ciudad = try { child.child("ciudad").value<String>() } catch (e: Exception) { "" }
                            val lat = try { child.child("lat").value<Double>() } catch (e: Exception) { 0.0 }
                            val lng = try { child.child("lng").value<Double>() } catch (e: Exception) { 0.0 }
                            val dias = try { child.child("dias").value<String>() } catch (e: Exception) { "" }
                            infoList.add(ParadaInfo(nombre = nombre, ciudad = ciudad, lat = lat, lng = lng, dias = dias))
                        }
                        _paradasInfo.value = infoList
                    }
                } catch (_: Exception) {}
                // Cargar info de puntos de subida
                try {
                    val db2 = network.FirebaseClient.database
                    val snap2 = db2.reference("catalogos/puntosSubidaInfo").valueEvents.first()
                    if (snap2.exists) {
                        val subidaList = mutableListOf<ParadaInfo>()
                        snap2.children.forEach { child ->
                            val nombre = child.key ?: return@forEach
                            val ciudad = try { child.child("ciudad").value<String>() } catch (e: Exception) { "" }
                            val lat = try { child.child("lat").value<Double>() } catch (e: Exception) { 0.0 }
                            val lng = try { child.child("lng").value<Double>() } catch (e: Exception) { 0.0 }
                            val hora = try { child.child("hora").value<String>() } catch (e: Exception) { "" }
                            val dias = try { child.child("dias").value<String>() } catch (e: Exception) { "" }
                            subidaList.add(ParadaInfo(nombre = nombre, ciudad = ciudad, lat = lat, lng = lng, hora = hora, dias = dias))
                        }
                        _puntosSubidaInfo.value = subidaList
                    }
                } catch (_: Exception) {}
            } catch (e: Exception) {
                _paradas.value = emptyList()
            }
        }
    }

    /** Filtra paradas por ciudad de destino del viaje actual */
    fun getParadasFiltradas(): List<String> {
        // La bajada es en la ciudad DESTINO del tramo actual. En la VUELTA (destino→origen)
        // esa ciudad es el ORIGEN original (ej. ida Qro→CDMX baja en CDMX; vuelta CDMX→Qro baja en Qro).
        val ciudadBajada = if (_tramoActual.value == Tramo.VUELTA) _origen.value else _destino.value
        val info = _paradasInfo.value
        if (ciudadBajada.isBlank() || info.isEmpty()) return _paradas.value
        val deLaCiudad = info.filter { it.ciudad.contains(ciudadBajada, ignoreCase = true) }
        // Una parada marcada solo para sábado no se ofrece en un viaje entre semana
        // (y al revés). Sin fecha todavía, no se filtra.
        val fecha = fechaTramo()
        val filtradas = (if (fecha.isNotBlank()) {
            deLaCiudad.filter { puntoAplicaEnFecha(it.dias, fecha) }.ifEmpty { deLaCiudad }
        } else deLaCiudad).map { it.nombre }
        return filtradas.ifEmpty { _paradas.value }
    }

    private val initialSeats = List(13) { index -> Seat(id = index + 1) }
    private val _seats = MutableStateFlow(initialSeats)
    val seats: StateFlow<List<Seat>> = _seats.asStateFlow()

    private val _layoutVehiculo = MutableStateFlow<VehiculoConfig?>(null)
    val layoutVehiculo: StateFlow<VehiculoConfig?> = _layoutVehiculo.asStateFlow()

    private val _isLoading = MutableStateFlow(false)
    val isLoading: StateFlow<Boolean> = _isLoading.asStateFlow()

    private val _isProcessing = MutableStateFlow(false)
    val isProcessing: StateFlow<Boolean> = _isProcessing.asStateFlow()

    private val _pasajeros = MutableStateFlow<Map<Int, PasajeroInfo>>(emptyMap())
    val pasajeros: StateFlow<Map<Int, PasajeroInfo>> = _pasajeros.asStateFlow()

    val idSesionActual = org.example.abigail.obtenerIdSesionPersistente()

    private val _tiempoRestanteMilis = MutableStateFlow(0L)
    val tiempoRestanteMilis: StateFlow<Long> = _tiempoRestanteMilis.asStateFlow()

    private val _mostrarTemporizador = MutableStateFlow(false)
    val mostrarTemporizador: StateFlow<Boolean> = _mostrarTemporizador.asStateFlow()

    private var timerJob: kotlinx.coroutines.Job? = null

    private val _mostrarAviso10Minutos = MutableStateFlow(false)
    val mostrarAviso10Minutos: StateFlow<Boolean> = _mostrarAviso10Minutos.asStateFlow()

    private val _errorAsientosGanados = MutableStateFlow(false)
    val errorAsientosGanados: StateFlow<Boolean> = _errorAsientosGanados.asStateFlow()

    private val _sesionExpiradaPorAbandono = MutableStateFlow(false)
    val sesionExpiradaPorAbandono: StateFlow<Boolean> = _sesionExpiradaPorAbandono.asStateFlow()

    private val _boletoPendiente = MutableStateFlow<Boleto?>(null)
    val boletoPendiente: StateFlow<Boleto?> = _boletoPendiente.asStateFlow()

    fun toggleIdaYVuelta(esIdaVuelta: Boolean) {
        _esIdaYVuelta.value = esIdaVuelta
        if (!esIdaVuelta) {
            _fechaVuelta.value = ""
            _horarioVuelta.value = ""
            _horariosVueltaDisponibles.value = emptyList()
        }
    }

    // FIX #10: Validar formato YYYY-MM-DD para que comparación por string funcione correctamente
    private fun esFechaValida(fecha: String): Boolean {
        if (fecha.length != 10) return false
        val partes = fecha.split("-")
        if (partes.size != 3) return false
        return partes[0].length == 4 && partes[1].length == 2 && partes[2].length == 2 &&
            partes.all { it.all { c -> c.isDigit() } }
    }

    fun seleccionarFechaVuelta(nuevaFecha: String) {
        if (!esFechaValida(nuevaFecha)) return
        // No permitir fecha de vuelta anterior a la fecha de ida
        if (nuevaFecha < _fecha.value) return
        _fechaVuelta.value = nuevaFecha
        filtrarHorariosVuelta(nuevaFecha)
    }

    private fun filtrarHorariosVuelta(fechaSeleccionada: String) {
        viewModelScope.launch {
            // La ruta de vuelta es destino-origen (invertida)
            val rutaVuelta = "${_destino.value} - ${_origen.value}"
            val horariosReales = repository.getHorariosProgramados(rutaVuelta, fechaSeleccionada)
            val fechaHoy = org.example.abigail.obtenerFechaHoy()
            val minutosAhora = org.example.abigail.obtenerMinutosActuales()

            val filtrados = if (fechaSeleccionada < fechaHoy) {
                emptyList()
            } else if (fechaSeleccionada == fechaHoy) {
                horariosReales.filter { parseHorarioAMinutos(it) > minutosAhora }
            } else {
                horariosReales
            }
            _horariosVueltaDisponibles.value = filtrados
            if (_horarioVuelta.value !in filtrados) {
                _horarioVuelta.value = filtrados.firstOrNull() ?: ""
            }
        }
    }

    fun actualizarBusqueda() {
        if (_origen.value.isNotBlank() && _destino.value.isNotBlank() && _fecha.value.isNotBlank()) {
            filtrarHorarios(_fecha.value)
        } else {
            _horariosDisponibles.value = emptyList()
            _horario.value = ""
        }
        // Si tiene vuelta seleccionada, actualizar también
        if (_esIdaYVuelta.value && _fechaVuelta.value.isNotBlank()) {
            filtrarHorariosVuelta(_fechaVuelta.value)
        }
    }


    fun seleccionarFecha(nuevaFecha: String) {
        if (!esFechaValida(nuevaFecha)) return
        _fecha.value = nuevaFecha
        actualizarBusqueda()
    }

    private fun filtrarHorarios(fechaSeleccionada: String) {
        viewModelScope.launch {
            val rutaActual = "${_origen.value} - ${_destino.value}"
            val horariosReales = repository.getHorariosProgramados(rutaActual, fechaSeleccionada)
            val fechaHoy = org.example.abigail.obtenerFechaHoy()
            val minutosAhora = org.example.abigail.obtenerMinutosActuales()

            val filtrados = if (fechaSeleccionada < fechaHoy) {
                emptyList()
            } else if (fechaSeleccionada == fechaHoy) {
                horariosReales.filter { horarioString ->
                    parseHorarioAMinutos(horarioString) > minutosAhora
                }
            } else {
                horariosReales
            }
            _horariosDisponibles.value = filtrados
            if (_horario.value !in filtrados) {
                _horario.value = filtrados.firstOrNull() ?: ""
            }
        }
    }

    private fun parseHorarioAMinutos(horarioStr: String): Int {
        return try {
            val partes = horarioStr.split(" ")
            val tiempo = partes[0].split(":")
            var horas = tiempo[0].toInt()
            val minutos = tiempo[1].toInt()
            val amPm = partes.getOrNull(1)?.uppercase() ?: ""

            if (amPm == "PM" && horas < 12) horas += 12
            if (amPm == "AM" && horas == 12) horas = 0

            (horas * 60) + minutos
        } catch (e: Exception) {
            0
        }
    }

    fun avanzarAAsientos() {
        _tramoActual.value = Tramo.IDA
        loadOccupiedSeats()
        cargarPuntoSubida()
        _currentStep.value = BookingStep.ELEGIR_ASIENTOS
    }

    private fun cargarPuntoSubida() {
        viewModelScope.launch {
            try {
                val ruta = rutaTramo()
                val db = network.FirebaseClient.database
                val basePath = "viajes_programados/$ruta/${fechaTramo()}/${horarioTramo()}"
                val snap = db.reference(basePath).valueEvents.first()
                val puntoSubidaRaw = if (snap.exists) {
                    try { snap.child("puntoSubida").value<String>() } catch (e: Exception) { "" }
                } else ""
                // Soportar múltiples puntos separados por |
                if (puntoSubidaRaw.contains("|")) {
                    _puntosSubidaViaje.value = puntoSubidaRaw.split("|").filter { it.isNotBlank() }
                    _puntoSubida.value = _puntosSubidaViaje.value.firstOrNull() ?: ""
                } else {
                    _puntosSubidaViaje.value = if (puntoSubidaRaw.isNotBlank()) listOf(puntoSubidaRaw) else emptyList()
                    _puntoSubida.value = puntoSubidaRaw
                }
            } catch (e: Exception) {
                _puntoSubida.value = ""
                _puntosSubidaViaje.value = emptyList()
            }
        }
    }

    fun seleccionarPuntoSubida(punto: String) {
        _puntoSubida.value = punto
    }

    fun escucharAsientosEnVivo(ruta: String, fecha: String, horario: String): Flow<List<Int>> {
        return repository.observarAsientosNoDisponibles(ruta, fecha, horario, idSesionActual)
    }

    fun actualizarAsientosEnVivo(ocupados: List<Int>) {
        _seats.update { currentList ->
            currentList.map { seat ->
                if (seat.status == SeatStatus.SELECTED) seat
                else if (seat.id in ocupados) seat.copy(status = SeatStatus.OCCUPIED)
                else seat.copy(status = SeatStatus.AVAILABLE)
            }
        }
    }

    fun iniciarCuentaRegresiva(tiempoFinal: Long) {
        timerJob?.cancel()
        _mostrarTemporizador.value = true
        timerJob = viewModelScope.launch {
            while (tiempoFinal > org.example.abigail.obtenerMilisegundosActuales()) {
                _tiempoRestanteMilis.value =
                    tiempoFinal - org.example.abigail.obtenerMilisegundosActuales()
                kotlinx.coroutines.delay(1000)
            }
            finalizarSesionPorTiempo()
        }
    }

    fun finalizarSesionPorTiempo() {
        liberarMisAsientosBloqueados()
    }

    /**
     * Detiene el contador SIN liberar asientos. Se llama al mandar al usuario a pagar.
     *
     * A partir de ahí los asientos los sostiene el servidor (bloqueos/{asiento}/pago_pendiente,
     * que escribe la CF con ~1h). Si el contador siguiera corriendo mientras el cliente paga
     * en el navegador, al vencer marcaría "sesión expirada", lo botaría a la selección de
     * asientos e intentaría liberar los lugares que acababa de pagar.
     */
    fun detenerCuentaRegresivaPorPago() {
        timerJob?.cancel()
        timerJob = null
        _mostrarTemporizador.value = false
        _tiempoRestanteMilis.value = 0L
    }

    /**
     * Libera en Firebase TODOS los asientos que actualmente tengo seleccionados o bloqueados,
     * y los devuelve al estado AVAILABLE localmente. También resetea el flujo a la selección
     * de asientos para que el usuario pueda volver a elegir.
     */
    private fun liberarMisAsientosBloqueados() {
        val rutaActual = rutaTramo()
        val fechaActual = fechaTramo()
        val horarioActual = horarioTramo()

        val seleccionados = _seats.value
            .filter { it.status == SeatStatus.SELECTED }
            .map { it.id }

        if (seleccionados.isEmpty()) return
        if (rutaActual.isBlank() || fechaActual.isBlank() || horarioActual.isBlank()) return

        // FIX #13: UI update dentro del launch, después de que Firebase confirme la liberación.
        // Antes la UI se actualizaba fuera del launch (inmediatamente), lo que causaba
        // inconsistencia si Firebase fallaba.
        viewModelScope.launch {
            seleccionados.forEach { asiento ->
                try {
                    repository.liberarAsientoBloqueado(rutaActual, fechaActual, horarioActual, asiento)
                } catch (e: Exception) {
                }
            }

            _seats.update { list ->
                list.map { if (it.id in seleccionados) it.copy(status = SeatStatus.AVAILABLE) else it }
            }
            _pasajeros.value = emptyMap()
            _mostrarTemporizador.value = false
            _tiempoRestanteMilis.value = 0L

            if (_currentStep.value != BookingStep.BUSCAR_VIAJE &&
                _currentStep.value != BookingStep.ELEGIR_ASIENTOS) {
                _currentStep.value = BookingStep.ELEGIR_ASIENTOS
                _sesionExpiradaPorAbandono.value = true
            }
        }

    }
    fun cancelarReservaActual() {
        timerJob?.cancel()
        timerJob = null
        liberarMisAsientosBloqueados()
        _currentStep.value = BookingStep.BUSCAR_VIAJE
        _pasajeros.value = emptyMap()
    }

    /** Pasajeros guardados de cada tramo (para el resumen). */
    fun pasajerosIdaResumen(): List<PasajeroInfo> = pasajerosIdaGuardados.ifEmpty { _pasajeros.value.values.toList() }
    fun pasajerosVueltaResumen(): List<PasajeroInfo> = pasajerosVueltaGuardados
    /** Punto de subida (abordaje) de cada tramo — para el resumen. */
    fun puntoSubidaIdaResumen(): String = puntoSubidaIdaGuardado.ifBlank { _puntoSubida.value }
    fun puntoSubidaVueltaResumen(): String = puntoSubidaVueltaGuardado

    /** Total (sin IVA) de cada tramo, con sus propios extras. */
    fun totalTramoIda(): Int = CalculoPrecios.calcularTotal(pasajerosIdaResumen(), _precios.value, false, false)
    fun totalTramoVuelta(): Int = if (_esIdaYVuelta.value) CalculoPrecios.calcularTotal(pasajerosVueltaGuardados, _precios.value, false, false) else 0

    /** Subtotal (sin IVA) real de la compra: ida + vuelta con extras propios por tramo. */
    fun subtotalEstimado(): Int {
        val ida = pasajerosIdaGuardados.ifEmpty { _pasajeros.value.values.toList() }
        return if (_esIdaYVuelta.value) {
            CalculoPrecios.calcularTotalIdaVuelta(ida, pasajerosVueltaGuardados, _precios.value, false)
        } else {
            CalculoPrecios.calcularTotal(ida, _precios.value, false, false)
        }
    }

    fun avanzarAManifiesto() {
        // En el tramo de VUELTA, reusar nombre/teléfono/correo de los pasajeros de ida (por orden de asiento).
        // La parada de bajada y maletas/mascota se ajustan en este tramo.
        if (_tramoActual.value == Tramo.VUELTA && pasajerosIdaGuardados.isNotEmpty()) {
            val seatsOrdenados = _pasajeros.value.keys.sorted()
            val nuevo = mutableMapOf<Int, PasajeroInfo>()
            seatsOrdenados.forEachIndexed { idx, seatId ->
                val base = _pasajeros.value[seatId] ?: PasajeroInfo(asiento = seatId)
                val ida = pasajerosIdaGuardados.getOrNull(idx)
                // Reusar SOLO identidad/contacto de ida. Maletas/mascota/parada de VUELTA son
                // independientes (puede llevar maleta de ida pero no regresarla) → arrancan en blanco.
                nuevo[seatId] = if (ida != null) base.copy(
                    nombre = ida.nombre,
                    telefono = ida.telefono,
                    correo = ida.correo,
                    maletasExtras = 0,
                    llevaMascota = false,
                    parada = ""
                ) else base
            }
            _pasajeros.value = nuevo
        }
        _currentStep.value = BookingStep.LLENAR_MANIFIESTO
    }

    fun retrocederAAsientos() {
        _currentStep.value = BookingStep.ELEGIR_ASIENTOS
    }

    fun retrocederABuscador() {
        liberarMisAsientosBloqueados()
        _currentStep.value = BookingStep.BUSCAR_VIAJE
    }

    /**
     * Deja el formulario como recién abierto tras una compra COMPLETADA.
     *
     * NO libera asientos: ya se vendieron. Por eso no se puede reusar retrocederABuscador().
     * En web el reseteo lo hace la recarga al volver de Stripe; en la APK no hay recarga, así
     * que sin esto el comprador se queda viendo sus datos y el viaje ya pagado, y parece que
     * la compra no se completó.
     */
    fun limpiarTrasCompraExitosa() {
        _pasajeros.value = emptyMap()
        _seats.value = initialSeats
        _puntoSubida.value = ""
        _puntosSubidaViaje.value = emptyList()
        _esIdaYVuelta.value = false
        _fechaVuelta.value = ""
        _horarioVuelta.value = ""
        _tramoActual.value = Tramo.IDA
        _requiereFactura.value = false
        _rfcFactura.value = ""
        puntoSubidaIdaGuardado = ""
        puntoSubidaVueltaGuardado = ""
        _mostrarTemporizador.value = false
        _tiempoRestanteMilis.value = 0L
        timerJob?.cancel()
        timerJob = null
        // Si no se limpia, el aviso de "sesión expirada" reaparece y rebota a asientos.
        _sesionExpiradaPorAbandono.value = false
        _currentStep.value = BookingStep.BUSCAR_VIAJE
    }

    fun toggleSeatSelection(seatId: Int) {
        val seat = _seats.value.find { it.id == seatId } ?: return
        if (seat.status == SeatStatus.AVAILABLE) {
            _seats.update { list -> list.map { if (it.id == seatId) it.copy(status = SeatStatus.SELECTED) else it } }
            _pasajeros.update { it + (seatId to PasajeroInfo(asiento = seatId)) }
        } else if (seat.status == SeatStatus.SELECTED) {
            _seats.update { list -> list.map { if (it.id == seatId) it.copy(status = SeatStatus.AVAILABLE) else it } }
            _pasajeros.update { it - seatId }
            if (_mostrarTemporizador.value) {
                val rutaActual = rutaTramo()
                val fechaActual = fechaTramo()
                val horarioActual = horarioTramo()
                viewModelScope.launch {
                    try {
                        repository.liberarAsientoBloqueado(rutaActual, fechaActual, horarioActual, seatId)
                    } catch (e: Exception) {
                    }
                }
            }
        }
    }

    fun intentarAvanzarYBloquear() {
        viewModelScope.launch {
            _isLoading.value = true
            val seleccionados =
                _seats.value.filter { it.status == SeatStatus.SELECTED }.map { it.id }
            // El bloqueo va SIEMPRE contra el tramo que se está eligiendo. Antes se armaba
            // con origen/destino/_fecha/_horario fijos, que son los de la IDA: al elegir los
            // asientos de la VUELTA se apartaban lugares del viaje de ida (fantasmas sobre
            // asientos ajenos) y los de regreso quedaban sin apartar, así que otro comprador
            // podía llevárselos durante los 10 minutos. toggleSeatSelection ya usaba las
            // funciones de tramo, así que bloquear y liberar tampoco coincidían.
            val rutaActual = rutaTramo()
            val fechaActual = fechaTramo()
            val horarioActual = horarioTramo()

            var todosBloqueados = true
            var limiteGlobal: Long = 0L

            for (asiento in seleccionados) {
                val limite = repository.bloquearAsientoTemporal(
                    rutaActual,
                    fechaActual,
                    horarioActual,
                    asiento,
                    idSesionActual
                )
                if (limite != null) {
                    limiteGlobal = limite
                } else {
                    todosBloqueados = false
                    break
                }
            }

            if (todosBloqueados) {
                iniciarCuentaRegresiva(limiteGlobal)
                _mostrarAviso10Minutos.value = true
            } else {
                seleccionados.forEach {
                    repository.liberarAsientoBloqueado(
                        rutaActual,
                        fechaActual,
                        horarioActual,
                        it
                    )
                }
                _seats.update { list -> list.map { if (it.id in seleccionados) it.copy(status = SeatStatus.AVAILABLE) else it } }
                _pasajeros.value = emptyMap()
                _errorAsientosGanados.value = true
            }
            _isLoading.value = false
        }
    }

    fun cerrarAvisoEIrAManifiesto() {
        _mostrarAviso10Minutos.value = false
        avanzarAManifiesto()
    }

    fun cerrarErrorAsientos() {
        _errorAsientosGanados.value = false
    }

    fun updatePassengerInfo(seatId: Int, info: PasajeroInfo) {
        _pasajeros.update { currentMap ->
            currentMap + (seatId to info)
        }
    }


    fun loadOccupiedSeats() {
        viewModelScope.launch {
            _isLoading.value = true
            val rutaActual = rutaTramo()
            val fechaActual = fechaTramo()
            val horarioActual = horarioTramo()

            // Cargar layout del vehículo para ESTE tramo (ida o vuelta pueden tener vehículos distintos)
            val layout = repository.obtenerLayoutDeViaje(rutaActual, fechaActual, horarioActual)
            _layoutVehiculo.value = layout

            // Determinar la capacidad (de layout o default)
            val capacidad = layout?.capacidad ?: 13
            if (_seats.value.size != capacidad) {
                _seats.value = List(capacidad) { index -> Seat(id = index + 1) }
            }

            val occupiedIds = repository.getOccupiedSeats(rutaActual, fechaActual, horarioActual)

            _seats.update { currentList ->
                currentList.map { seat ->
                    if (seat.id in occupiedIds) seat.copy(status = SeatStatus.OCCUPIED)
                    else seat.copy(status = SeatStatus.AVAILABLE)
                }
            }
            _isLoading.value = false
        }
    }

    private val _ultimoBoleto = MutableStateFlow<Boleto?>(null)
    val ultimoBoleto: StateFlow<Boleto?> = _ultimoBoleto.asStateFlow()
    fun irAPagar() {
        _currentStep.value = BookingStep.VER_BOLETOS
    }

    fun confirmarPagoYReservar(onNavigateToPayment: () -> Unit) {
        // Bloqueo por versión: una app desactualizada no debe crear reservas con esquema viejo.
        if (network.VersionGate.bloqueado) {
            _errorFormulario.value = "Tu app está desactualizada. Actualízala para completar tu compra."
            return
        }
        // Los datos por tramo ya quedaron guardados en los holders al confirmar cada manifiesto.
        val asientosIda = asientosIdaGuardados
        val pasajerosIda = pasajerosIdaGuardados
        if (asientosIda.isEmpty()) return

        val asientosVlt = if (_esIdaYVuelta.value) asientosVueltaGuardados else emptyList()
        val pasajerosVlt = if (_esIdaYVuelta.value) pasajerosVueltaGuardados else emptyList()

        // Cálculo en función pura testeada (CalculoPrecios). El servidor recalcula y manda.
        val totalAcumulado = if (_esIdaYVuelta.value) {
            CalculoPrecios.calcularTotalIdaVuelta(
                pasajerosIda = pasajerosIda,
                pasajerosVuelta = pasajerosVlt,
                precios = _precios.value,
                requiereFactura = _requiereFactura.value
            )
        } else {
            CalculoPrecios.calcularTotal(
                pasajeros = pasajerosIda,
                precios = _precios.value,
                esIdaYVuelta = false,
                requiereFactura = _requiereFactura.value
            )
        }
        val rutaIda = "${_origen.value} - ${_destino.value}"
        val rutaVuelta = "${_destino.value} - ${_origen.value}"

        _boletoPendiente.value = Boleto(
            asientos = asientosIda,
            pasajeros = pasajerosIda,
            totalPagado = totalAcumulado,
            fechaViaje = _fecha.value,
            horario = _horario.value,
            origenDestino = rutaIda,
            puntoSubida = puntoSubidaIdaGuardado.ifBlank { _puntoSubida.value },
            urlSalida = _urlSalida.value,
            timestamp = org.example.abigail.obtenerMilisegundosActuales().toString(),
            esIdaYVuelta = _esIdaYVuelta.value,
            fechaVuelta = if (_esIdaYVuelta.value) _fechaVuelta.value else "",
            horarioVuelta = if (_esIdaYVuelta.value) _horarioVuelta.value else "",
            rutaVuelta = if (_esIdaYVuelta.value) rutaVuelta else "",
            asientosVuelta = asientosVlt,
            pasajerosVuelta = pasajerosVlt,
            puntoSubidaVuelta = if (_esIdaYVuelta.value) puntoSubidaVueltaGuardado else "",
            requiereFactura = _requiereFactura.value,
            rfc = if (_requiereFactura.value) _rfcFactura.value else "",
            razonSocial = if (_requiereFactura.value) _razonSocialFactura.value else "",
            regimenFiscal = if (_requiereFactura.value) _regimenFiscalFactura.value else "",
            usoCfdi = if (_requiereFactura.value) _usoCfdiFactura.value else "",
            cpFiscal = if (_requiereFactura.value) _cpFiscalFactura.value else ""
        )

        // Navegar a la pantalla de pago
        onNavigateToPayment()
    }

    fun finalizarCompraYSalir() {
        timerJob?.cancel()
        timerJob = null
        _ultimoBoleto.value = null
        _boletoPendiente.value = null
        _pasajeros.value = emptyMap()

        val capacidad = _layoutVehiculo.value?.capacidad ?: 13
        _seats.value = List(capacidad) { index -> Seat(id = index + 1) }
        _layoutVehiculo.value = null

        _mostrarTemporizador.value = false
        _tiempoRestanteMilis.value = 0L
        _whatsappUrl.value = null
        _esIdaYVuelta.value = false
        _fechaVuelta.value = ""
        _horarioVuelta.value = ""
        _horariosVueltaDisponibles.value = emptyList()
        // Limpiar tramo + holders de ida y vuelta
        _tramoActual.value = Tramo.IDA
        asientosIdaGuardados = emptyList(); pasajerosIdaGuardados = emptyList(); puntoSubidaIdaGuardado = ""
        asientosVueltaGuardados = emptyList(); pasajerosVueltaGuardados = emptyList(); puntoSubidaVueltaGuardado = ""
        _requiereFactura.value = false
        _rfcFactura.value = ""
        _razonSocialFactura.value = ""
        _regimenFiscalFactura.value = ""
        _usoCfdiFactura.value = ""
        _cpFiscalFactura.value = ""
        _currentStep.value = BookingStep.BUSCAR_VIAJE
    }

    fun validarYAvanzarAResumen() {
        val pasajerosActuales = _pasajeros.value.values.toList()

        val hayCamposVacios = pasajerosActuales.any { it.nombre.isBlank() || it.telefono.isBlank() }

        if (hayCamposVacios) {
            _errorFormulario.value =
                "Por favor, ingresa el Nombre Completo y Teléfono de TODOS los pasajeros."
            return
        }

        // Teléfono debe tener 10 dígitos (México) para que el chofer/soporte pueda contactar
        val telefonoInvalido = pasajerosActuales.any { it.telefono.length != 10 }
        if (telefonoInvalido) {
            _errorFormulario.value =
                "El Teléfono de cada pasajero debe tener 10 dígitos."
            return
        }

        val faltaParada = pasajerosActuales.any { it.parada.isBlank() }
        if (faltaParada) {
            _errorFormulario.value =
                "Por favor, selecciona una Parada de descenso para TODOS los pasajeros."
            return
        }

        val titular = pasajerosActuales.firstOrNull()
        if (titular?.correo.isNullOrBlank()) {
            _errorFormulario.value =
                "Por favor, ingresa un Correo Electrónico para el pasajero titular."
            return
        }

        // El correo del titular debe ser válido: ahí llegan los boletos PDF
        if (!correoEsValido(titular?.correo ?: "")) {
            _errorFormulario.value =
                "Ingresa un Correo Electrónico válido para el titular (ej. nombre@dominio.com)."
            return
        }

        _errorFormulario.value = null

        val asientosSel = _seats.value.filter { it.status == SeatStatus.SELECTED }.map { it.id }.sorted()
        val pasajerosSel = _pasajeros.value.values.toList()

        if (_tramoActual.value == Tramo.IDA && _esIdaYVuelta.value) {
            // Guardar IDA y pasar al tramo de VUELTA (su propio layout/asientos/pasajeros)
            asientosIdaGuardados = asientosSel
            pasajerosIdaGuardados = pasajerosSel
            puntoSubidaIdaGuardado = _puntoSubida.value
            cargarTramoVuelta()
        } else {
            // Guardar el tramo actual y avanzar al resumen
            if (_tramoActual.value == Tramo.VUELTA) {
                asientosVueltaGuardados = asientosSel
                pasajerosVueltaGuardados = pasajerosSel
                puntoSubidaVueltaGuardado = _puntoSubida.value
            } else {
                asientosIdaGuardados = asientosSel
                pasajerosIdaGuardados = pasajerosSel
                puntoSubidaIdaGuardado = _puntoSubida.value
            }
            _currentStep.value = BookingStep.RESUMEN_COMPRA
        }
    }

    /** Prepara el tramo de VUELTA: limpia selección, carga su layout/asientos/puntos. */
    private fun cargarTramoVuelta() {
        _tramoActual.value = Tramo.VUELTA
        _pasajeros.value = emptyMap()
        // loadOccupiedSeats reconstruye _seats con la capacidad del vehículo de vuelta
        loadOccupiedSeats()
        cargarPuntoSubida()
        _mostrarTemporizador.value = false
        _currentStep.value = BookingStep.ELEGIR_ASIENTOS
    }

    /** Validación pragmática de correo: algo@dominio.tld, sin espacios. */
    private fun correoEsValido(correo: String): Boolean {
        val c = correo.trim()
        val at = c.indexOf("@")
        if (at <= 0 || c.contains(" ")) return false
        if (c.indexOf("@", at + 1) != -1) return false // un solo @
        val dominio = c.substring(at + 1)
        return dominio.contains(".") && !dominio.startsWith(".") && !dominio.endsWith(".")
    }

    fun limpiarErrorFormulario() {
        _errorFormulario.value = null
    }


    fun retrocederAlManifiesto() {
        _currentStep.value = BookingStep.LLENAR_MANIFIESTO
    }
    fun liberarBloqueosSiSaleSinPagar() {
        if (_mostrarTemporizador.value) {
            liberarMisAsientosBloqueados()
        }
    }


    private fun cargarPrecios() {
        viewModelScope.launch {
            try {
                _precios.value = catalogRepository.getPrecios()
            } catch (e: Exception) {
                _precios.value = mapOf(
                    "base" to 350,
                    "maleta" to 150,
                    "mascota" to 150
                )
            }
        }
    }

    fun cerrarAvisoSesionExpirada() {
        _sesionExpiradaPorAbandono.value = false
    }
    // FIX #5: Limpiar referencia singleton al destruir el ViewModel
    fun cleanup() {
        if (instanciaActiva === this) instanciaActiva = null
    }

    companion object {
        var instanciaActiva: BookingViewModel? = null
    }
}